import React, { useMemo } from 'react';
import { PageParams } from '../types/PageParams';
import { useParams } from 'react-router-dom';
import CompanyInformationDetails from '../components/CompanyInformationDetails';
import { convertIdToNumber } from '../utils/idConversion';
import { useExternalCompany } from 'src/hooks/use-external-company';
import CompanyBaseInfoBox from '../../components/company/CompanyBaseInfoBox';
import { LoadingPage } from 'src/components/LoadingPage/LoadingPage';
import { Alert } from '@innovationnorway/cl-react';
import { Trans, useTranslation } from 'react-i18next';
import useCompanyContract from '../hooks/use-company-contract';
import useHotjar from 'src/hooks/use-hotjar';
import { useAuth } from 'src/features/authorization';

const CompanyInfo: React.FC = () => {
  const { t: tSiva } = useTranslation('siva');
  const { contractId } = useParams<PageParams>();
  const {
    contract,
    isLoading: contractIsLoading,
    company,
  } = useCompanyContract(convertIdToNumber(contractId));
  const { data: externalCompany, isLoading: companyIsLoading } = useExternalCompany(
    company!.organizationNumber,
  );
  const hotjar = useHotjar();
  const { isLoading: authIsLoading } = useAuth();

  const isLoading = useMemo(
    () => contractIsLoading || companyIsLoading || authIsLoading,
    [contractIsLoading, companyIsLoading, authIsLoading],
  );

  React.useEffect(() => {
    if (!isLoading) hotjar.log('SIVA_COMPANY_DETAILS_OPENED');
  }, [isLoading, hotjar]);

  if (isLoading) {
    return <LoadingPage />;
  }

  if (!companyIsLoading && !externalCompany) {
    return (
      <Alert data-color="info" className="margin-bottom--4">
        <p>
          <Trans
            t={tSiva}
            i18nKey={'companyTabs.about.notification.text1'}
            values={{ name: contract?.name }}
            components={{
              '1': <strong>{contract?.name}</strong>,
            }}
          >
            Kunne ikke finne bedriftsinfo for <strong>{contract?.name}</strong>
          </Trans>
        </p>

        <p>{tSiva('companyTabs.about.notification.text2')}</p>
      </Alert>
    );
  }

  return (
    <div className="company-info">
      {contract && company && externalCompany && (
        <>
          <CompanyBaseInfoBox company={externalCompany} hideName={true} />
          <CompanyInformationDetails
            clusterMemberId={contract.clusterMemberId ?? ''}
            organizationNumber={company.organizationNumber ?? ''}
          />
        </>
      )}
    </div>
  );
};

export default CompanyInfo;
